// Header — full-width app bar. Wordmark left, nav + profile right.
// On mobile, hamburger left toggles ThreadList; assets icon right toggles AssetsPanel.

import type { Thread } from '../types';

type NavTarget = 'chat' | 'settings';

interface Props {
  currentScreen: NavTarget;
  onNavigate: (to: NavTarget) => void;
  activeThread?: Thread | null;
  /** Mobile-only — toggle the left ThreadList drawer (hidden on md+). */
  onToggleSidebar?: () => void;
  /** Mobile-only — toggle the right AssetsPanel drawer (hidden on lg+). */
  onToggleAssets?: () => void;
}

export function Header({
  currentScreen, onNavigate,
  onToggleSidebar, onToggleAssets,
}: Props) {
  return (
    <header className="sticky top-0 z-40 bg-bg/85 backdrop-blur-md border-b border-hairline px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 min-w-0">
        {/* Mobile hamburger — only visible below md */}
        {onToggleSidebar && (
          <button
            onClick={onToggleSidebar}
            aria-label="Toggle thread list"
            className="md:hidden p-1.5 -ml-1 rounded hover:bg-ink/[0.06] text-text2 hover:text-ink"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        )}

        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
          {/* ElementUSA logo */}
          <img
            src="/elementusa-logo.webp"
            alt="ElementUSA"
            className="h-5 sm:h-[22px] w-auto shrink-0"
          />

          {/* Separator */}
          <div className="hidden sm:block h-6 w-px bg-border shrink-0" />

          {/* Element IQ wordmark — ELEMENT text + [IQ] periodic-cell */}
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="hidden sm:inline font-display text-[15px] text-ink leading-none tracking-tight">
              ELEMENT
            </span>
            <span className="relative inline-flex flex-col items-center justify-center w-7 h-7 bg-ink rounded">
              <span className="absolute top-[2px] left-[3px] text-[6.5px] font-bold text-accent leading-none">00</span>
              <span className="font-display text-[12px] text-bg leading-none mt-0.5">IQ</span>
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <nav className="flex items-center gap-1">
          <NavLink active={currentScreen === 'chat'} onClick={() => onNavigate('chat')}>Chat</NavLink>
          <NavLink active={currentScreen === 'settings'} onClick={() => onNavigate('settings')}>Settings</NavLink>
        </nav>

        {/* Mobile assets icon — only visible below lg */}
        {onToggleAssets && (
          <button
            onClick={onToggleAssets}
            aria-label="Toggle assets panel"
            className="lg:hidden p-1.5 rounded hover:bg-ink/[0.06] text-text2 hover:text-ink"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" />
              <rect x="14" y="3" width="7" height="7" />
              <rect x="14" y="14" width="7" height="7" />
              <rect x="3" y="14" width="7" height="7" />
            </svg>
          </button>
        )}

        <div className="w-8 h-8 bg-ink text-bg rounded-full grid place-items-center text-xs font-semibold cursor-pointer shrink-0">
          MS
        </div>
      </div>
    </header>
  );
}

function NavLink({ active, children, onClick }: { active: boolean; children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`text-xs font-medium px-2.5 sm:px-3 py-1.5 rounded-full transition-colors ${
        active ? 'bg-ink text-bg' : 'text-text2 hover:text-ink'
      }`}
    >
      {children}
    </button>
  );
}
